import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  Bot,
  Boxes,
  ChevronRight,
  FileText,
  FlaskConical,
  History,
  LayoutDashboard,
  Loader2,
  LogOut,
  Megaphone,
  MessagesSquare,
  Newspaper,
  RefreshCw,
  ShieldCheck,
  TriangleAlert,
  Waypoints,
} from "lucide-react";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { AdminApi, ApiError, agentIdentifier } from "@/api";
import { CONVERSATIONS_DEFAULT_SEARCH } from "@/components/conversations/conversationsSearch";
import {
  AdminShellContext,
  NON_ADMIN_GUIDANCE,
  isNotAdminError,
  useSession,
  type AdminShellValue,
  type SamsAccessState,
} from "@/components/layout/AdminShellContext";
import { pageMetaFor } from "@/components/layout/pageMeta";
import { TEST_RESULT_DEFAULT_SEARCH, TEST_RESULT_ROUTE } from "@/components/test-result/testResultSearch";
import {
  ALL_TEST_CASES_DEFAULT_SEARCH,
  ALL_TEST_CASES_ROUTE,
} from "@/components/test-cases/allTestCasesSearch";
import { DASHBOARD_ROUTE } from "@/components/dashboard/dashboardSearch";
import { lastSearch } from "./lastSearch";
import { REFERENCE_REVIEW_SEARCH, REFERENCE_ROUTE } from "@/components/reference-answer/referenceReview";
import {
  Banner,
  Button,
  EmptyState,
  Input,
  Label,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarSeparator,
} from "@/components/ui";
import { defaultApiBaseUrl, defaultReplayLabBaseUrl, defaultSamsApiBaseUrl } from "@/config";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import type { AdminPromptState, AgentState, EvalCounts, SkillCatalog } from "@/types";

/**
 * A sub-item's count, trailing its label as in the artifact. Absent when the
 * count is unknown — a 0 nobody counted would read as "nothing here".
 * (`SidebarMenuBadge` is placed for top-level buttons and floats loose here.)
 */
function NavCount({ value }: { value: number | undefined }) {
  if (typeof value !== "number") return null;
  return <span className="ml-auto shrink-0 text-xs font-medium text-sidebar-foreground tabular-nums">{value}</span>;
}

/**
 * The root route's layout.
 *
 * Built on shadcn's Sidebar rather than a hand-rolled `<aside>`: the structure
 * this screen needs — labelled groups, a non-clickable parent with an indented
 * sub-list, badges, a footer pinned to the bottom — is exactly what that
 * component models, and the app is already on the `base-nova` (Base UI) track.
 *
 * The shell still owns what it owned before routing (the shared
 * loading/error/notice state and the three datasets) and hands it to routes
 * through `AdminShellContext`. Routes read it and pass the same props to the
 * same page components.
 */
export function AdminShell() {
  const session = useSession();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const [apiBaseUrl, setApiBaseUrl] = useState(defaultApiBaseUrl);
  const [agents, setAgents] = useState<AgentState[]>([]);
  const [skillCatalog, setSkillCatalog] = useState<SkillCatalog | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [promptState, setPromptState] = useState<AdminPromptState | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [counts, setCounts] = useState<EvalCounts | null>(null);
  // Reference versions not passed yet; scoring does not read a new one until it is.
  const [referenceWaiting, setReferenceWaiting] = useState<number | undefined>(undefined);
  const [samsAccess, setSamsAccess] = useState<SamsAccessState>({ state: "loading" });
  // Test Cases' sub-tabs stay folded until its parent is clicked.
  const [testCasesOpen, setTestCasesOpen] = useState(false);

  const api = useMemo(
    () => AdminApi.forSamsLogin(apiBaseUrl, session.access_token, defaultReplayLabBaseUrl,
                                defaultSamsApiBaseUrl || undefined),
    [apiBaseUrl, session.access_token],
  );
  const selectedAgent = agents.find((agent) => agentIdentifier(agent) === selectedId);

  // A non-admin's 403 names its user id; show the request-access guidance instead.
  const showError = (err: unknown) =>
    setError(isNotAdminError(err) ? NON_ADMIN_GUIDANCE : err instanceof Error ? err.message : String(err));
  const flash = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 2500);
  };

  const loadAgents = async () => {
    setLoading(true);
    setError("");
    try {
      const [nextAgents, catalog] = await Promise.all([
        api.listAgents(),
        api.skillCatalog(),
      ]);
      setAgents(nextAgents);
      setSkillCatalog(catalog);
      if (!selectedId && nextAgents[0]) setSelectedId(agentIdentifier(nextAgents[0]));
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadPromptLab = async () => {
    setLoading(true);
    setError("");
    try {
      setPromptState(await api.promptState());
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The banner is about the screen that raised it: a failed agent read must
  // not follow the person to Issue Candidates, whose own calls succeeded.
  useEffect(() => {
    setError("");
  }, [pathname]);

  // Who may see the Admins screen. Asked again whenever the client changes
  // (the API base URL field rebuilds it) and after an admin change
  // (`refreshSamsAccess`); the server still decides each call.
  const accessFrom = async (client: AdminApi): Promise<SamsAccessState> => {
    try {
      const me = await client.samsAccess();
      return me.admin === true ? { state: "admin", me } : { state: "denied", reason: "SAMS 관리자가 아닙니다." };
    } catch (err) {
      if (isNotAdminError(err)) return { state: "denied", reason: NON_ADMIN_GUIDANCE, code: "FORBIDDEN" };
      const reason = err instanceof ApiError && err.code ? `${err.message} (${err.code})`
        : err instanceof Error ? err.message : String(err);
      return { state: "denied", reason, code: err instanceof ApiError ? err.code : undefined };
    }
  };

  useEffect(() => {
    let current = true;
    setSamsAccess({ state: "loading" });
    void accessFrom(api).then((next) => {
      if (current) setSamsAccess(next);
    });
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const refreshSamsAccess = async () => {
    api.forgetSamsAccess();
    setSamsAccess(await accessFrom(api));
  };

  const [reloadKey, setReloadKey] = useState(0);

  // The badges and the brand tag. A failure hides them rather than
  // showing a banner: they are a hint, and every screen still works.
  const loadCounts = async () => {
    void api.listReferences({ review_status: "not_passed", limit: 1 }).then(
      (list) => setReferenceWaiting(list?.counts?.not_passed),
      () => setReferenceWaiting(undefined),
    );
    try {
      setCounts(await api.getEvalCounts());
    } catch {
      setCounts(null);
    }
  };
  // Read again on landing in a Test Cases screen and on its Reload, so a page
  // just made or run there shows up in the counts.
  const countsTrigger = pathname.startsWith("/test-cases") ? pathname : "";
  useEffect(() => {
    void loadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, countsTrigger]);
  const environment = typeof counts?.environment === "string" ? counts.environment : "";

  const value: AdminShellValue = {
    session,
    api,
    apiBaseUrl,
    loading,
    saving,
    setSaving,
    showError,
    flash,
    reloadKey,
    samsAccess,
    refreshSamsAccess,
    agents,
    setAgents,
    skillCatalog,
    selectedId,
    setSelectedId,
    selectedAgent,
    promptState,
    setPromptState,
    loadPromptLab,
  };

  // Which loader the header's Reload calls. The Test Cases screens fetch
  // their own data, so for them Reload bumps `reloadKey` and they re-read.
  // Conversations and both engagement pages carry their own controls, so the
  // button is absent there rather than present and inert.
  const bumpReload = async () => setReloadKey((key) => key + 1);
  const reload = pathname.startsWith("/agents")
    ? loadAgents
    : pathname.startsWith("/prompt-lab")
      ? loadPromptLab
      : /^\/(dashboard|test-cases\/(all|batch|result|references))/.test(pathname)
        ? bumpReload
        : null;

  const meta = pageMetaFor(pathname);
  const notAdmin = samsAccess.state === "denied" && samsAccess.code === "FORBIDDEN";

  return (
    <AdminShellContext.Provider value={value}>
      <SidebarProvider style={{ "--sidebar-width": "236px" } as CSSProperties}>
        <Sidebar>
          <SidebarHeader>
            <div className="flex items-center gap-2.5 px-2 py-1">
              <span className="grid size-6 shrink-0 place-items-center overflow-hidden rounded-md bg-brand">
                {/* No size class: Preflight's `max-width:100%; height:auto`
                    already fits the square source to the plate, and the plate
                    clips the corners.
                    Decorative — "SAMS" beside it already names the app, so an
                    alt here would just be read twice. */}
                <img src="/synapse_logo_white.png" alt="Synapse Logo" />
              </span>
              <span className="font-semibold tracking-tight">SAMS</span>
              {/* Which backend this console is talking to — its APP_ENV, not
                  a guess from the URL. Red when that is production. */}
              {environment && (
                <span
                  className={cn(
                    "ml-auto rounded-full border px-1.5 font-mono text-[9.5px] tracking-widest uppercase",
                    /^prod/i.test(environment)
                      ? "border-destructive/40 text-destructive"
                      : "border-border text-muted-foreground",
                  )}
                  title="이 콘솔이 붙은 백엔드의 APP_ENV"
                >
                  {environment}
                </span>
              )}
            </div>
          </SidebarHeader>

          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>Overview</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={pathname.startsWith("/dashboard")}
                      onClick={() =>
                        navigate({ to: DASHBOARD_ROUTE })
                      }
                    >
                      <LayoutDashboard />
                      <span>Dashboard</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Agent</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={pathname.startsWith("/agents")}
                      onClick={() => navigate({ to: "/agents" })}
                    >
                      <Bot />
                      <span>Synapse Agents</span>
                    </SidebarMenuButton>
                    {agents.length > 0 && <SidebarMenuBadge>{agents.length}</SidebarMenuBadge>}
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={pathname.startsWith("/prompt-lab")}
                      onClick={() => navigate({ to: "/prompt-lab" })}
                    >
                      <FileText />
                      <span>Prompt Lab</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Evaluation</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    {/* The tab is its screens — there is no "Test Cases" page
                        to land on, so the parent only folds its sub-tabs.
                        Folded by default; while folded it still reads as the
                        current section when one of its screens is open. */}
                    <SidebarMenuButton
                      aria-expanded={testCasesOpen}
                      aria-controls="test-cases-subtabs"
                      onClick={() => setTestCasesOpen((open) => !open)}
                      className={cn(
                        "font-semibold text-sidebar-foreground",
                        !testCasesOpen && pathname.startsWith("/test-cases") && "text-sidebar-accent-foreground",
                      )}
                    >
                      <FlaskConical />
                      <span>Test Cases</span>
                      <ChevronRight
                        aria-hidden
                        className={cn("ml-auto transition-transform", testCasesOpen && "rotate-90")}
                      />
                    </SidebarMenuButton>
                    <SidebarMenuSub id="test-cases-subtabs" hidden={!testCasesOpen}>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton isActive={pathname.startsWith("/test-cases/candidates")} onClick={() => navigate({ to: "/test-cases/candidates" })}>
                          <span>문제 식별</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          isActive={pathname.startsWith("/test-cases/all")}
                          // Back to where the tab was left, as in the artifact.
                          onClick={() =>
                            navigate({
                              to: ALL_TEST_CASES_ROUTE,
                              search: lastSearch(ALL_TEST_CASES_ROUTE, ALL_TEST_CASES_DEFAULT_SEARCH),
                            })
                          }
                        >
                          <span>All Test Cases</span>
                          <NavCount value={counts?.test_cases?.total} />
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          isActive={pathname.startsWith("/test-cases/batch")}
                          onClick={() => navigate({ to: "/test-cases/batch" })}
                        >
                          <span>Batch Test</span>
                          <NavCount value={counts?.batch_pages?.total} />
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          isActive={pathname.startsWith("/test-cases/result")}
                          onClick={() =>
                            navigate({
                              to: TEST_RESULT_ROUTE,
                              search: lastSearch(TEST_RESULT_ROUTE, TEST_RESULT_DEFAULT_SEARCH),
                            })
                          }
                        >
                          <span>Test Result</span>
                          {/* Pages that have run — the ones with a Matrix. */}
                          <NavCount value={counts?.batch_pages?.with_runs} />
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          isActive={pathname.startsWith(REFERENCE_ROUTE)}
                          onClick={() =>
                            navigate({ to: REFERENCE_ROUTE, search: lastSearch(REFERENCE_ROUTE, REFERENCE_REVIEW_SEARCH) })
                          }
                        >
                          <span>Reference Answer</span>
                          {/* Versions not passed yet — only a passed one is scored. */}
                          {typeof referenceWaiting === "number" && referenceWaiting > 0 && (
                            <span
                              className="ml-auto shrink-0 rounded-full bg-accent px-1.5 text-xs font-medium text-accent-foreground tabular-nums"
                              title="통과하지 않은 Reference 버전"
                            >
                              {referenceWaiting}
                            </span>
                          )}
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          isActive={pathname.startsWith("/test-cases/cause-loop")}
                          onClick={() => navigate({ to: "/test-cases/cause-loop" })}
                        >
                          {/* Active TCs whose Reference a person passed, by score gap (LOOPFLOW H). */}
                          <span>루프 대상</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </SidebarMenuItem>

                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={pathname.startsWith("/conversations")}
                      onClick={() =>
                        navigate({
                          to: "/conversations",
                          search: CONVERSATIONS_DEFAULT_SEARCH,
                        })
                      }
                    >
                      <MessagesSquare />
                      <span>Conversations</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {/* Only for an admin SAMS confirmed (`/me`); the route gates too. */}
                  {samsAccess.state === "admin" && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={pathname.startsWith("/backend-errors")}
                        onClick={() => navigate({ to: "/backend-errors" })}
                      >
                        <TriangleAlert />
                        <span>Backend Errors</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Engagement</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={pathname.startsWith("/proactive-messages")}
                      onClick={() => navigate({ to: "/proactive-messages" })}
                    >
                      <Megaphone />
                      <span>Proactive Messages</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={pathname.startsWith("/home-feed")}
                      onClick={() => navigate({ to: "/home-feed" })}
                    >
                      <Newspaper />
                      <span>Home Feed</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {/* Only for an admin SAMS confirmed (`/me`); the route gates too. */}
            {samsAccess.state === "admin" && (
              <SidebarGroup>
                <SidebarGroupLabel>Settings</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={pathname.startsWith("/admins")}
                        onClick={() => navigate({ to: "/admins" })}
                      >
                        <ShieldCheck />
                        <span>Admins</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton isActive={pathname.startsWith("/models")} onClick={() => navigate({ to: "/models" })}>
                        <Boxes />
                        <span>모델 카탈로그</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton isActive={pathname.startsWith("/call-sites")} onClick={() => navigate({ to: "/call-sites" })}>
                        <Waypoints />
                        <span>호출 지점</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton isActive={pathname.startsWith("/changelog")} onClick={() => navigate({ to: "/changelog" })}>
                        <History />
                        <span>변경 기록</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
          </SidebarContent>

          {/* A little more bottom room than the component's `p-2`: the
              sign-out row is the last thing in the rail and 8px reads as
              "nearly cut off" even when it is not. */}
          <SidebarFooter className="pb-3">
            {/* Not in the mockup, but real: pointing the console at another
                deployment is how prod gets inspected.
                The button beside it is the other half — changing the URL only
                rebuilds the client, so without a way to re-fetch on the spot
                the field appears to do nothing until you navigate. */}
            <div className="grid gap-1.5">
              {/* Label and input are split rather than using TextField so the
                  button can sit in a row with the input alone. `items-stretch`
                  then matches its height to the input's instead of guessing a
                  size that drifts the moment the field's padding changes. */}
              <Label htmlFor="api-base-url" className="text-xs font-normal text-muted-foreground">
                API base URL
              </Label>
              <div className="flex items-stretch gap-1.5">
                <Input
                  id="api-base-url"
                  className="min-w-0 flex-1 text-xs"
                  value={apiBaseUrl}
                  onChange={(event) => setApiBaseUrl(event.target.value)}
                />
                <Button
                  variant="outline"
                  className="min-h-0 w-9 shrink-0 px-0"
                  onClick={() => {
                    void loadAgents();
                    void loadCounts();
                  }}
                  disabled={loading}
                  aria-label="Reload agents"
                  title="바뀐 주소로 에이전트를 다시 불러옵니다"
                >
                  {loading ? <Loader2 className="animate-spin" /> : <RefreshCw />}
                </Button>
              </div>
            </div>
            <SidebarSeparator className="mx-0" />
            {/* No left padding: the whole footer shares one left/right
                grid. The email starts where the "API base URL" label and
                the input's border start, and the sign-out button ends
                where the reload button ends. Indenting this block alone
                breaks both edges at once. */}
            <div className="flex items-center gap-2">
              <div className="grid min-w-0 flex-1 gap-1">
                <span className="truncate text-xs text-sidebar-foreground">{session.user.email}</span>
                <span className="text-[9.5px] tracking-widest text-muted-foreground uppercase">
                  admin
                </span>
              </div>
              <Button
                variant="outline"
                size="icon"
                className="w-9"
                onClick={() => void supabase?.auth.signOut()}
                aria-label="Sign out"
                title="Sign out"
              >
                <LogOut />
              </Button>
            </div>
          </SidebarFooter>
        </Sidebar>

        <SidebarInset>
          <header className="border-b border-border bg-card px-6 pt-4.5 pb-4">
            <div className="flex flex-wrap items-start gap-4">
              <div className="min-w-0">
                {/* Preflight zeroes margins, so the three lines need their
                    own rhythm or they read as one block. */}
                <p className="mb-1 text-[10px] tracking-[0.12em] text-muted-foreground uppercase">
                  {meta.crumb}
                </p>
                <h1 className="text-[21px] font-semibold tracking-tight">{meta.title}</h1>
                <p className="mt-1.5 max-w-[62ch] text-[13px] text-muted-foreground">{meta.desc}</p>
              </div>
              {reload && (
                <Button variant="outline" className="ml-auto" onClick={reload} disabled={loading}>
                  {loading ? <Loader2 className="animate-spin" /> : <RefreshCw />}
                  Reload
                </Button>
              )}
            </div>
          </header>
          <div className="min-w-0 px-6 pt-5 pb-14">
            {notAdmin ? (
              // Every SAMS screen answers this login with 403: say what to do once,
              // instead of a page of failures that each name the user id.
              <EmptyState title="SAMS 관리자 권한이 없습니다" body={NON_ADMIN_GUIDANCE} />
            ) : (
              <>
                {error && <Banner tone="error">{error}</Banner>}
                {notice && <Banner tone="success">{notice}</Banner>}
                <Outlet />
              </>
            )}
          </div>
        </SidebarInset>
      </SidebarProvider>
    </AdminShellContext.Provider>
  );
}
